import Vue from 'vue'
import config from '@/configurations/config-environment'
import { BaseService } from '../baseService'

const dataws = config.apis.dataws

/**
 * Client for the new OVAM grondverzetsonderzoek-meldingen API (OvamController in DataWS).
 * Completely separate from the old CadastralOvamService: this talks to the modern REST route
 * `api/ovam/grondverzetsonderzoek-meldingen` instead of the legacy ITWDataRestService_V1_0 pattern.
 * The auteur's organisatieNummer is a static backend value (Web.config), not passed from here.
 * Token/credential handling lives entirely on the backend.
 */
export class GrondverzetService extends BaseService {
  _meldingenUrl() {
    return `${dataws}api/ovam/grondverzetsonderzoek-meldingen`
  }

  /**
   * Registers a new grondverzetsonderzoek-melding (POST).
   * The organisatieNummer is a static backend value (Web.config), so it is not passed here.
   * @param {object} request GrondverzetsonderzoekMeldingRequest
   * @returns {Promise<object>} GrondverzetsonderzoekMeldingResponse
   */
  submitMelding(request) {
    const data = { authorisationRequest: this.getAuthorizationObject(), melding: request }
    return Vue.http
      .post(this._meldingenUrl(), data, {
        headers: { 'Content-Type': 'application/json' }
      })
      .then((resp) => resp.data)
  }

  /**
   * Fetches an existing melding (GET).
   * @param {string|number} id
   * @returns {Promise<object>} GrondverzetsonderzoekMeldingResponse
   */
  getMelding(id) {
    // GET is query-based (REST-idiomatic): the identity travels as flat query params, bound on the
    // backend to [FromUri] AuthorisationRequest. (POST/PUT wrap it in the body; a GET wrapper would
    // trip the global ClaimsAuthorize filter, so GET stays flat.)
    return Vue.http.get(`${this._meldingenUrl()}/${id}`, { params: this.getAuthorizationObject() }).then((resp) => resp.data)
  }

  /**
   * Updates an existing melding (PUT).
   * @param {string|number} id
   * @param {object} request GrondverzetsonderzoekMeldingRequest
   * @returns {Promise<object>} GrondverzetsonderzoekMeldingResponse
   */
  updateMelding(id, request) {
    const data = { authorisationRequest: this.getAuthorizationObject(), melding: request }
    return Vue.http
      .put(`${this._meldingenUrl()}/${id}`, data, {
        headers: { 'Content-Type': 'application/json' }
      })
      .then((resp) => resp.data)
  }

  _credentialsUrl() {
    return `${dataws}api/ovam/credentials`
  }

  /**
   * Reads whether this license has OVAM credentials configured. The secret is never returned,
   * only whether it is set (hasClientSecret) plus the client id.
   * @returns {Promise<{clientId: string, hasClientId: boolean, hasClientSecret: boolean}>}
   */
  getCredentialStatus() {
    return Vue.http.get(this._credentialsUrl(), { params: this.getAuthorizationObject() }).then((resp) => resp.data)
  }

  /**
   * Saves the per-license OVAM credentials. A blank clientSecret keeps the existing one.
   * @param {{clientId: string, clientSecret: string}} credentials
   */
  saveCredentials(credentials) {
    const data = { authorisationRequest: this.getAuthorizationObject(), credentials }
    return Vue.http
      .put(this._credentialsUrl(), data, {
        headers: { 'Content-Type': 'application/json' }
      })
      .then((resp) => resp.data)
  }
}
